import { computed, ref, watch, type Ref } from 'vue'
import { watchDebounced } from '@vueuse/core'
import apicore from '@/plugins/api'
import { AFP_REGIONS } from 'afpnews-api'
import { preferredLocale } from '@/plugins/i18n'
import { normalize } from '@/lib/normalize'

const FACET_QUERY_KEYS = [
  'slug',
  'creator',
  'country',
  'region',
  'city',
  'genre',
  'event',
  'topic',
  'lang'
] as const
export type FacetKey = (typeof FACET_QUERY_KEYS)[number]

const FACET_PATTERN = new RegExp(`^(${FACET_QUERY_KEYS.join('|')}):(.*)$`, 'i')

// Opérateurs booléens AFP (majuscules) ou parenthèses de groupement : signalent
// une requête composée, pas une simple autocomplétion de facette.
const BOOLEAN_QUERY = /\s(?:AND|OR|NOT)\s|[()]/

const FACET_TOKEN = new RegExp(`\\b(?:${FACET_QUERY_KEYS.join('|')}):`, 'i')

// True dès que la saisie contient un token de facette AFP (`genre:`, `country:`…),
// qu'elle soit simple ou composée. Sert à distinguer une requête structurée d'une
// question en langage naturel.
export function containsFacetToken(input: string): boolean {
  return FACET_TOKEN.test(input)
}

export interface FacetSuggestion {
  facet: FacetKey
  value: string
  count: number
}

const cache = new Map<string, Promise<{ name: string; count: number }[]>>()

function fetchFacet(
  facet: FacetKey,
  lang: string
): Promise<{ name: string; count: number }[]> {
  // `region` n'est pas une facette AFP : ses comptes sont la somme de ceux de ses pays
  if (facet === 'region') {
    return fetchFacet('country', lang).then(countries =>
      Object.entries(AFP_REGIONS)
        .map(([name, codes]) => ({
          name,
          count: countries
            .filter(c => (codes as readonly string[]).includes(c.name))
            .reduce((sum, c) => sum + c.count, 0)
        }))
        .toSorted((a, b) => b.count - a.count)
    )
  }
  const cacheKey = `${facet}:${lang}`
  let p = cache.get(cacheKey)
  if (!p) {
    p = apicore
      .list(facet, {
        size: 100,
        dateFrom: 'now-2d',
        source: 'afp',
        langs: [lang]
      })
      .then(r =>
        r.keywords.flatMap(k =>
          k.name ? [{ name: k.name, count: k.count }] : []
        )
      )
      .catch(() => {
        cache.delete(cacheKey)
        return []
      })
    cache.set(cacheKey, p)
  }
  return p
}

function isFacetKey(value: string): value is FacetKey {
  return (FACET_QUERY_KEYS as readonly string[]).includes(value)
}

export function parseFacetInput(
  input: string
): { facet: FacetKey; prefix: string } | null {
  const trimmed = input.trim()
  if (BOOLEAN_QUERY.test(trimmed)) return null
  const m = FACET_PATTERN.exec(trimmed)
  if (!m) return null
  // Le groupe capturé provient de FACET_PATTERN, construit à partir de
  // FACET_QUERY_KEYS : il correspond toujours à l'une de ces clés.
  const key = m[1].toLowerCase()
  if (!isFacetKey(key)) return null
  return { facet: key, prefix: m[2] }
}

export interface UseFacetSuggestionsOptions {
  class?: Ref<string | undefined>
  limit?: number
}

export function useFacetSuggestions(
  input: Ref<string>,
  options: UseFacetSuggestionsOptions = {}
) {
  const limit = options.limit ?? 8
  const suggestions = ref<FacetSuggestion[]>([])
  const loading = ref(false)

  const lang = computed(() =>
    options.class?.value === 'picture' ? 'en' : preferredLocale.value
  )

  watch(input, value => {
    if (!parseFacetInput(value)) suggestions.value = []
  })

  watchDebounced(
    [input, lang],
    async ([value, currentLang]) => {
      const parsed = parseFacetInput(value)
      if (!parsed) {
        suggestions.value = []
        return
      }
      loading.value = true
      try {
        const all = await fetchFacet(parsed.facet, currentLang)
        const np = normalize(parsed.prefix)
        const filtered = np
          ? all.filter(k => normalize(k.name).startsWith(np))
          : all
        suggestions.value = filtered.slice(0, limit).map(k => ({
          facet: parsed.facet,
          value: k.name,
          count: k.count
        }))
      } finally {
        loading.value = false
      }
    },
    { debounce: 300 }
  )

  return { suggestions, loading }
}
